import React, { useEffect, useRef, useState } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { Field, ImageField } from '@sitecore-jss/sitecore-jss-nextjs';
import { RegisterMergeText } from '../RegistrationMergeFields';
import { useDispatch, useSelector } from 'react-redux';
import { clearRegistrationData } from '../../../features/registrationSlice';
import { useRouter } from 'next/router';
import { RootState } from '../../../store/store';
import { post } from '../../../lib/utils/axiosFetcher';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import StThankYouItemWrapper from './ThankYou.style';
import UpdateEmail from '../../shared/UpdateEmail/UpdateEmail';
import { EMAIL_TEMPLATES } from '../../../constants';
import { fetchProfileDetailsForCR } from '../../../lib/utils/axiosCDPFetcher';
import { setProfileData } from '../../../features/crProfileSlice';
import { PATIENT_TYPE_ADULT } from '../../../constants';
import { sendWelcomeSms } from '../../../components/shared/Sms/helper';
import UiBox from '../../shared/Box';
import UiText from '../../shared/Typography/UiText';
import UiHeading from '../../shared/Heading';
import { getCookie } from '../../../lib/utils/enrollmentUtil';
import { postDeduplicationAudit } from '../../ContactInfo/helper';
import { postLeads } from '../../ContactInfo/helper';

interface IThankYouFields {
  changeEmailBtnText: Field<string>;
  image: ImageField;
  resendBtnText: Field<string>;
  resendText: Field<string>;
  subTitle: Field<string>;
  thankYouText: Field<string>;
  title: Field<string>;
  updatePopUpEmailLabel: Field<string>;
  updatePopUpSubTitle: Field<string>;
  updatePopUpTitle: Field<string>;
  confirmButtonText: Field<string>;
  cancelButtonnText: Field<string>;
  emailInvalidErrorMessage: Field<string>;
  emailAlreadyExistErrorMessage: Field<string>;
  backgroundColor: Field<string>;
}
interface IThankYou {
  fields: IThankYouFields;
}

function removeTrailingSlash(url: string) {
  if (url.endsWith('/')) {
    return url.slice(0, -1);
  }
  return url;
}

const LoaderBackdrop = (props: { open: boolean }) => {
  return (
    <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={props.open}>
      <CircularProgress color="inherit" />
    </Backdrop>
  );
};

const ThankYou: React.FC<IThankYou> = (props) => {
  // const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down(500));
  const dispatch = useDispatch();
  const router = useRouter();
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings: any = sitecoreContext?.siteSettings;
  const source = siteSettings?.source?.fields?.name?.value || 'CR';
  const [isLoading, setIsLoading] = useState(true);
  const isSmsConsented = useSelector(
    (state: RootState) => state?.registrationDetails?.contactPreference?.SMS
  );
  const isMobile = useSelector(
    (state: RootState) => state?.registrationDetails?.contactPreference?.isMobile
  );
  const mobileNumber = useSelector(
    (state: RootState) => state?.registrationDetails?.contactPreference?.contactNumber
  );
  const registrationDetails = useSelector((state: RootState) => state?.registrationDetails);
  const resendCounterRef = useRef(0);

  // enum for emailType
  // Registration-Welcome
  // Manage-Profile

  const callDapApiSendEmailAllDetails = async (registrationDetails: any) => {
    try {
      const mailingListId = siteSettings?.sendMailingListId?.value || '';
      const emailType = EMAIL_TEMPLATES.REGISTRATION_WELCOME;
      const baseUrl = siteSettings?.baseUrl?.value || '';
      const userConditions = registrationDetails?.selectedCOIList?.map(
        (condition: { key: any }) => condition.key
      );
      const therapeuticArea = userConditions ? userConditions?.join(', ') : '';
      const relevantItemsList = registrationDetails?.patientDetails?.map(
        (arrayIndex: { index: number }) => arrayIndex.index + 1
      );
      const selectedRelevantItems = relevantItemsList ? relevantItemsList?.join(', ') : '';
      const cleanBaseUrl = removeTrailingSlash(baseUrl);
      const myProfileEndpoint = siteSettings?.myProfileItemUrl?.value?.href || '';
      const myProfileUrl = `${cleanBaseUrl}${myProfileEndpoint}`;
      const payload = {
        mailingListId,
        source: source,
        baseUrl,
        myProfileUrl: myProfileUrl,
        communityPageLink: '',
        resendAttemptCount: resendCounterRef.current,
        subscriber: {
          emailAddress: registrationDetails?.registration?.email,
          name: `${registrationDetails?.aboutYourSelf?.firstName || ''} ${
            registrationDetails?.aboutYourSelf?.lastName || ''
          }`,
          firstName: registrationDetails?.aboutYourSelf?.firstName || '',
          lastName: registrationDetails?.aboutYourSelf?.lastName || '',
          phone: registrationDetails?.contactPreference?.contactNumber || '',
          dateOfBirth: registrationDetails?.dob?.dob || '',
          gender: registrationDetails?.dob?.gender || '',
          isEmailOptedIn: registrationDetails?.contactPreference?.Email || false,
          isSmsOptedIn: registrationDetails?.contactPreference?.SMS || false,
          isSecondaryConsentAccepted: registrationDetails?.contactPreference?.agree || false,
          preferredLanguage: sitecoreContext?.language as any,
          therapeuticArea,
          selectedRelevantItems,
          interestedInAtHomeTrials: registrationDetails?.locationDetails?.homeTrial
            ? registrationDetails?.locationDetails?.homeTrial?.toLowerCase() === 'yes'
            : false,
          myProfileLink: '',
          customUnsubscribeLink: '',
          crWelcomeEmailType: emailType,
          registrationDateTime: new Date().toISOString(),
          contactType: PATIENT_TYPE_ADULT,
          zipCode: registrationDetails?.locationDetails?.zipcode || '',
        },
        enrollmentId: '',
        siteSettingsId: sitecoreContext?.siteSettingsId as any,
        language: sitecoreContext?.language as any,
        isCrEmailCheck: false,
      };
      await post('/Send/AddContactsToCRMailingListWithAllDetail', payload);
    } catch (_error) {}
  };
  const callSendSmsEvent = async () => {
    try {
      const isSendSmsEnabled = siteSettings?.enableSmsSendFeature?.value;
      if (isSendSmsEnabled && isSmsConsented && isMobile) {
        const transactionalFromNumber = siteSettings?.transactionalFromNumber?.value;
        const pinPointProjectId = siteSettings?.pinPointProjectId?.value;
        const smsStudyName = siteSettings?.studyName?.value;
        const smsSponsor = siteSettings?.smsSponsor?.value;
        const sourceIdentifier = siteSettings?.smsSource?.fields?.name?.value || 'CR';
        const countryCode = siteSettings?.countryCode?.value;
        const smsRootPath = sitecoreContext?.smsRootPath;
        const smsExtensionData = {
          enrollmentId: '',
          smsType: 'Cr-Registration',
          transactionalFromNumber: transactionalFromNumber || '',
          pinPointProjectId: pinPointProjectId || '',
          smsStudyName: smsStudyName || '',
          smsSponsor: smsSponsor || '',
          sourceIdentifier: sourceIdentifier || '',
          countryCode: countryCode || '',
          smsRootPath: smsRootPath || '',
          mobileNumber: `+1${mobileNumber}` || '',
          websiteName: sitecoreContext?.site?.name,
        };
        sendWelcomeSms(smsExtensionData);
      }
    } catch (_error) {}
  };

  useEffect(() => {
    // This function will run when the page changes
    const handleRouteChange = () => {
      dispatch(clearRegistrationData());
    };

    // Set up a listener for route changes
    router.events.on('routeChangeStart', handleRouteChange);
    // Clean up the listener when the component unmounts
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [dispatch, router]);

  useEffect(() => {
    callDapApiSendEmailAllDetails(registrationDetails);
    callSendSmsEvent();
    postDeduplicationAudit();
    postLeads('Registration');
    const initialCall = async () => {
      const myProfileVerificationToken = sessionStorage.getItem('myProfileVerificationToken');
      if (myProfileVerificationToken) {
        try {
          const { data } = await fetchProfileDetailsForCR(myProfileVerificationToken);
          if (data) {
            dispatch(setProfileData(data));
          }
        } catch (_e) {}
      }
    };
    setTimeout(() => {
      initialCall();
      setIsLoading(false);
    }, 3000);
  }, []);
  // push to PatientExportAudit
  useEffect(() => {
    const allowAudit = process.env.NEXT_PUBLIC_ALLOW_PATIENT_EXPORT_AUDIT === 'true';
    if (!allowAudit) {
      return;
    }
    const guestRef = getCookie('bx_guest_ref');
    const bIdcookie = 'bid_' + process.env.NEXT_PUBLIC_CDP_CLIENT_KEY;
    const bIdVal = getCookie(bIdcookie);
    const browserId = window.Engage?.getBrowserId() || bIdVal;

    if (!guestRef) {
      return;
    }
    const payload = {
      guestRef: guestRef,
      browserId: browserId,
      dataSource: 'Registration',
      status: 'Requested',
      pointOfSale: sitecoreContext?.site?.name,
    };

    const postAuditHistory = async () => {
      try {
        await post('/Patient/AddPatientExportAudit', payload);
      } catch (error) {}
    };
    postAuditHistory();
  }, []);

  return (
    <Box id="thank-you" className="ui-thank-you">
      <LoaderBackdrop open={isLoading} />
      <Grid container justifyContent="center">
        <Grid container justifyContent="center" alignItems="center">
          <Grid item xs={12}>
            <UiBox
              styles={{
                paddingBottom: '10px',
              }}
            >
              <UiHeading className="title" align="center" size="h2" isChildren>
                <RegisterMergeText field={props.fields.title} />
              </UiHeading>
            </UiBox>

            <Typography className="subtitle" variant="subtitle1" align="center">
              <UiText className="ui-page-subtitle" color="default" align="center">
                <RegisterMergeText field={props.fields.subTitle} />
              </UiText>
            </Typography>
          </Grid>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <StThankYouItemWrapper
            style={{ backgroundColor: props.fields.backgroundColor?.value || '#E6F9F8' }}
            className="mail-box"
          >
            <UpdateEmail {...props} />
          </StThankYouItemWrapper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ThankYou;
